import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Users, Plus, Edit2, Trash2, Loader2, Check, X, Eye, EyeOff, Key, UserCheck, UserX
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminAdvisors({ authToken, projectId }) {
  const [advisors, setAdvisors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    is_active: true,
    project_id: projectId || "hypefactory"
  });
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    fetchAdvisors();
  }, [projectId]);

  const fetchAdvisors = async () => {
    setIsLoading(true);
    try {
      const params = projectId ? `?project_id=${projectId}` : '';
      const response = await axios.get(`${API}/admin/advisors${params}`, getAuthHeaders());
      setAdvisors(response.data.advisors || []);
    } catch (error) {
      toast.error("Fehler beim Laden der Berater");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }

    setIsSaving(true);
    try {
      await axios.post(`${API}/admin/advisors`, {
        ...formData,
        project_id: projectId || formData.project_id
      }, getAuthHeaders());
      toast.success("Berater erfolgreich erstellt!");
      setShowCreateModal(false);
      resetForm();
      fetchAdvisors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Bitte Name und E-Mail ausfüllen");
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(
        `${API}/admin/advisors/${selectedAdvisor.id}`,
        { 
          name: formData.name, 
          email: formData.email,
          is_active: formData.is_active
        },
        getAuthHeaders()
      );
      toast.success("Berater aktualisiert!");
      setShowEditModal(false);
      fetchAdvisors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Aktualisieren");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAdvisor) return;
    
    try {
      await axios.delete(`${API}/admin/advisors/${selectedAdvisor.id}`, getAuthHeaders());
      toast.success("Berater gelöscht!");
      setShowDeleteDialog(false);
      setSelectedAdvisor(null);
      fetchAdvisors();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }

    setIsSaving(true);
    try {
      await axios.post(
        `${API}/admin/advisors/${selectedAdvisor.id}/reset-password`,
        { new_password: newPassword },
        getAuthHeaders()
      );
      toast.success("Passwort zurückgesetzt!");
      setShowPasswordModal(false);
      setNewPassword("");
    } catch (error) {
      toast.error("Fehler beim Zurücksetzen des Passworts");
    } finally {
      setIsSaving(false);
    }
  };

  const openEditModal = (advisor) => {
    setSelectedAdvisor(advisor);
    setFormData({
      name: advisor.name || "",
      email: advisor.email || "",
      password: "",
      is_active: advisor.is_active !== false
    });
    setShowEditModal(true);
  };

  const openDeleteDialog = (advisor) => {
    setSelectedAdvisor(advisor);
    setShowDeleteDialog(true);
  };

  const openPasswordModal = (advisor) => {
    setSelectedAdvisor(advisor);
    setNewPassword("");
    setShowPasswordModal(true);
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", password: "", is_active: true, project_id: projectId || "hypefactory" });
    setShowPassword(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="advisors-title">
            Berater verwalten
          </h1>
          <p className="text-slate-500">Berater-Accounts erstellen und verwalten</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="rounded-lg bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700"
          data-testid="create-advisor-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Berater
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-900">Berater-Rolle</h3>
              <p className="text-sm text-emerald-700 mt-1">
                Berater können alle Leads einsehen, bearbeiten und E-Mails versenden. 
                Sie erhalten Benachrichtigungen bei neuen Leads. Login unter: <code className="bg-emerald-100 px-1 rounded">/berater</code>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advisors Table */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-400" />
            Berater ({advisors.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : advisors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Noch keine Berater vorhanden</p>
              <Button 
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                variant="outline"
                className="mt-4 rounded-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ersten Berater erstellen
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Letzter Login</TableHead>
                  <TableHead>Erstellt</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {advisors.map((advisor) => (
                  <TableRow key={advisor.id} data-testid={`advisor-row-${advisor.id}`}>
                    <TableCell className="font-medium">{advisor.name}</TableCell>
                    <TableCell className="text-slate-500">{advisor.email}</TableCell>
                    <TableCell>
                      {advisor.is_active !== false ? (
                        <Badge className="bg-green-100 text-green-700">
                          <UserCheck className="w-3 h-3 mr-1" />
                          Aktiv
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">
                          <UserX className="w-3 h-3 mr-1" />
                          Inaktiv
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {advisor.last_login ? formatDate(advisor.last_login) : "Noch nie"}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(advisor.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPasswordModal(advisor)}
                          className="rounded-lg text-slate-500 hover:text-amber-600"
                          title="Passwort zurücksetzen"
                          data-testid={`reset-password-${advisor.id}`}
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(advisor)}
                          className="rounded-lg text-slate-500 hover:text-blue-600"
                          title="Bearbeiten"
                          data-testid={`edit-advisor-${advisor.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(advisor)}
                          className="rounded-lg text-slate-500 hover:text-red-600"
                          title="Löschen"
                          data-testid={`delete-advisor-${advisor.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              Neuen Berater erstellen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Max Mustermann"
                className="rounded-lg"
                data-testid="create-advisor-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">E-Mail</Label>
              <Input
                id="create-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="berater@firma.de"
                className="rounded-lg"
                data-testid="create-advisor-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Passwort</Label>
              <div className="relative">
                <Input
                  id="create-password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Mindestens 6 Zeichen"
                  className="rounded-lg pr-10"
                  data-testid="create-advisor-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-lg">
              Abbrechen
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={isSaving}
              className="rounded-lg bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-create-advisor"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Berater bearbeiten
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Max Mustermann"
                className="rounded-lg"
                data-testid="edit-advisor-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">E-Mail</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="berater@firma.de"
                className="rounded-lg"
                data-testid="edit-advisor-email"
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Status</Label>
                <p className="text-sm text-slate-500">Berater kann sich einloggen</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="edit-advisor-status"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="rounded-lg">
              Abbrechen
            </Button>
            <Button 
              onClick={handleUpdate} 
              disabled={isSaving}
              className="rounded-lg bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-edit-advisor"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Passwort zurücksetzen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Neues Passwort für <strong>{selectedAdvisor?.name}</strong> setzen:
            </p>
            <div className="space-y-2">
              <Label htmlFor="new-password">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  className="rounded-lg pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)} className="rounded-lg">
              Abbrechen
            </Button>
            <Button 
              onClick={handleResetPassword} 
              disabled={isSaving || newPassword.length < 6}
              className="rounded-lg bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-reset-password"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
              Zurücksetzen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              Berater löschen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Berater <strong>{selectedAdvisor?.name}</strong> ({selectedAdvisor?.email}) wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-lg bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-advisor"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
